import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { auth } from '../firebase';
import { Colors, Radius, Shadow, Typography } from '../theme';
import type { RootStackParamList } from '../types';
import { useI18n } from '../i18n';

type Props = NativeStackScreenProps<RootStackParamList, 'ResetPassword'> & {
  onContinueToLogin: () => Promise<void>;
};

type Status = 'verifying' | 'ready' | 'submitting' | 'success' | 'error';

const logo = require('../assets/login_logo.png');

const getErrorMessage = (error: unknown, t: (key: string) => string) => {
  const code = typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code?: unknown }).code || '')
    : '';

  switch (code) {
    case 'auth/expired-action-code':
      return t('resetPassword.expiredLink');
    case 'auth/invalid-action-code':
      return t('resetPassword.invalidLink');
    case 'auth/weak-password':
      return t('resetPassword.weakPassword');
    case 'auth/network-request-failed':
      return t('resetPassword.networkError');
    default:
      return t('resetPassword.verifyFailed');
  }
};

export default function ResetPasswordScreen({ onContinueToLogin }: Props) {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const route = useRoute<Props['route']>();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const oobCode = route.params?.oobCode?.trim() || '';
  const [status, setStatus] = useState<Status>('verifying');
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    if (!oobCode) {
      setStatus('error');
      setMessage(t('resetPassword.missingCode'));
      return undefined;
    }

    setStatus('verifying');
    verifyPasswordResetCode(auth, oobCode)
      .then(email => {
        if (cancelled) return;
        setAccountEmail(email);
        setStatus('ready');
      })
      .catch(error => {
        if (cancelled) return;
        setStatus('error');
        setMessage(getErrorMessage(error, t));
      });

    return () => {
      cancelled = true;
    };
  }, [oobCode, t]);

  const submit = async () => {
    setMessage('');
    if (password.length < 6) {
      setMessage(t('resetPassword.weakPassword'));
      return;
    }
    if (password !== confirmPassword) {
      setMessage(t('resetPassword.mismatch'));
      return;
    }

    setStatus('submitting');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('success');
      setMessage(t('resetPassword.success'));
    } catch (error) {
      setStatus('ready');
      setMessage(getErrorMessage(error, t));
    }
  };

  const continueToLogin = async () => {
    await onContinueToLogin();
    if (navigation.canGoBack()) {
      navigation.popToTop();
    }
  };

  const busy = status === 'verifying' || status === 'submitting';

  return (
    <LinearGradient colors={['#0B1826', '#070B14', '#020409']} style={styles.container}>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />
      <View style={styles.orangeGlow} />
      <View style={styles.tealGlow} />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[
            styles.scroll,
            { paddingTop: insets.top + 36, paddingBottom: insets.bottom + 28 },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <Image source={logo} style={styles.logo} resizeMode="contain" />
          <Text style={styles.brand}>TONYTE</Text>
          <View style={styles.card}>
            <Text style={styles.title}>{t('resetPassword.title')}</Text>
            <Text style={styles.subtitle}>{t('resetPassword.subtitle')}</Text>

            {busy ? (
              <View style={styles.stateWrap}>
                <ActivityIndicator size="large" color="#FF7A00" />
                <Text style={styles.stateText}>
                  {status === 'verifying' ? t('resetPassword.verifying') : t('resetPassword.updating')}
                </Text>
              </View>
            ) : status === 'success' ? (
              <View style={styles.stateWrap}>
                <View style={[styles.stateIcon, styles.successIcon]}>
                  <Icon name="check" size={34} color="#86EFAC" />
                </View>
                <Text style={styles.successTitle}>{t('resetPassword.success')}</Text>
                <TouchableOpacity style={styles.primaryButton} onPress={continueToLogin} activeOpacity={0.84}>
                  <Text style={styles.primaryButtonText}>{t('resetPassword.continueToLogin')}</Text>
                </TouchableOpacity>
              </View>
            ) : status === 'error' ? (
              <View style={styles.stateWrap}>
                <View style={[styles.stateIcon, styles.errorIcon]}>
                  <Icon name="x" size={34} color="#FDA4AF" />
                </View>
                <Text style={styles.errorTitle}>{t('resetPassword.unavailable')}</Text>
                <Text style={styles.errorText}>{message}</Text>
                <TouchableOpacity style={styles.secondaryButton} onPress={continueToLogin} activeOpacity={0.84}>
                  <Text style={styles.secondaryButtonText}>{t('resetPassword.continueToLogin')}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.form}>
                {accountEmail ? (
                  <Text style={styles.accountEmail}>{t('resetPassword.resettingFor').replace('{email}', accountEmail)}</Text>
                ) : null}

                <Text style={styles.label}>{t('resetPassword.newPassword')}</Text>
                <View style={styles.passwordField}>
                  <Icon name="lock" size={18} color="#FF7A00" />
                  <TextInput
                    value={password}
                    onChangeText={setPassword}
                    placeholder={t('resetPassword.minimumCharacters')}
                    placeholderTextColor={Colors.textMuted}
                    secureTextEntry={!showPassword}
                    textContentType="newPassword"
                    autoCapitalize="none"
                    style={styles.input}
                  />
                  <TouchableOpacity onPress={() => setShowPassword(current => !current)} hitSlop={10}>
                    <Icon name={showPassword ? 'eye-off' : 'eye'} size={19} color={Colors.textMuted} />
                  </TouchableOpacity>
                </View>

                <Text style={styles.label}>{t('resetPassword.confirmPassword')}</Text>
                <TextInput
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  placeholder={t('resetPassword.enterAgain')}
                  placeholderTextColor={Colors.textMuted}
                  secureTextEntry={!showPassword}
                  textContentType="newPassword"
                  autoCapitalize="none"
                  style={[styles.passwordField, styles.confirmInput]}
                />

                {message ? <Text style={styles.formError}>{message}</Text> : null}

                <TouchableOpacity style={styles.primaryButton} onPress={submit} activeOpacity={0.84}>
                  <Text style={styles.primaryButtonText}>{t('resetPassword.updatePassword')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  orangeGlow: {
    position: 'absolute',
    width: 310,
    height: 310,
    borderRadius: 155,
    top: -150,
    left: -130,
    backgroundColor: 'rgba(255,122,0,0.13)',
  },
  tealGlow: {
    position: 'absolute',
    width: 280,
    height: 280,
    borderRadius: 140,
    bottom: -150,
    right: -120,
    backgroundColor: 'rgba(38,208,206,0.08)',
  },
  logo: {
    width: 82,
    height: 82,
    alignSelf: 'center',
  },
  brand: {
    marginTop: 8,
    marginBottom: 22,
    color: '#FF7A00',
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
    letterSpacing: 4,
    textAlign: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 430,
    alignSelf: 'center',
    padding: 24,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(9,17,29,0.92)',
    ...Shadow.strong,
  },
  title: {
    color: Colors.textPrimary,
    fontSize: Typography.xxl,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  subtitle: {
    marginTop: 8,
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    lineHeight: 21,
    textAlign: 'center',
  },
  stateWrap: {
    minHeight: 250,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 24,
  },
  stateText: {
    marginTop: 16,
    color: Colors.textSecondary,
    fontSize: Typography.sm,
  },
  stateIcon: {
    width: 68,
    height: 68,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successIcon: {
    backgroundColor: 'rgba(34,197,94,0.13)',
  },
  errorIcon: {
    backgroundColor: 'rgba(244,63,94,0.13)',
  },
  successTitle: {
    marginTop: 20,
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  errorTitle: {
    marginTop: 20,
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
  },
  errorText: {
    marginTop: 10,
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    lineHeight: 21,
    textAlign: 'center',
  },
  form: {
    marginTop: 28,
  },
  accountEmail: {
    marginBottom: 18,
    padding: 12,
    borderRadius: Radius.lg,
    color: Colors.textSecondary,
    backgroundColor: 'rgba(255,255,255,0.045)',
    fontSize: Typography.sm,
    textAlign: 'center',
  },
  label: {
    marginBottom: 8,
    color: 'rgba(255,255,255,0.8)',
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  passwordField: {
    minHeight: 52,
    marginBottom: 18,
    paddingHorizontal: 15,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(0,0,0,0.2)',
    flexDirection: 'row',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    paddingHorizontal: 12,
    color: Colors.textPrimary,
    fontSize: Typography.base,
  },
  confirmInput: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    paddingVertical: 13,
  },
  formError: {
    marginBottom: 16,
    padding: 12,
    borderRadius: Radius.lg,
    color: '#FECDD3',
    backgroundColor: 'rgba(244,63,94,0.09)',
    fontSize: Typography.sm,
    lineHeight: 20,
  },
  primaryButton: {
    width: '100%',
    minHeight: 52,
    marginTop: 22,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF7A00',
    ...Shadow.glow,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.extrabold,
  },
  secondaryButton: {
    width: '100%',
    minHeight: 52,
    marginTop: 22,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  secondaryButtonText: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
});
